import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GraduationCap, User, Lock, Eye, EyeOff, UserPlus, ArrowLeft, MessageCircle, WifiOff, AlertTriangle, X } from "lucide-react";

function LoginForm({ onSwitchToRegister }) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [connectionError, setConnectionError] = useState(false);
  const { studentLogin } = useAuth();
  const navigate = useNavigate();

  const [errorDetail, setErrorDetail] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setConnectionError(false);
    setErrorDetail(null);
    if (!username || !password) {
      toast.error("Please enter your username and password");
      return;
    }
    setLoading(true);
    try {
      await studentLogin(username, password);
      toast.success("Welcome back!");
      navigate("/student-dashboard");
    } catch (error) {
      console.error("Student login error:", error);
      let userMsg = "";
      let debugInfo = "";

      if (error.response) {
        const status = error.response.status;
        const detail = error.response.data?.detail;
        const dataRaw = typeof error.response.data === "string"
          ? error.response.data.substring(0, 200)
          : JSON.stringify(error.response.data).substring(0, 200);
        userMsg = (detail && typeof detail === "string") ? detail : `Server error (${status})`;
        debugInfo = `Status: ${status} | Response: ${dataRaw}`;
      } else if (error.request) {
        userMsg = "No response from server — connection may be blocked";
        debugInfo = `Code: ${error.code || "unknown"} | Message: ${error.message}`;
        setConnectionError(true);
      } else {
        userMsg = error.message || "Unknown error";
        debugInfo = `Type: setup | ${error.message}`;
      }
      toast.error(userMsg);
      setErrorDetail({ userMsg, debugInfo, timestamp: new Date().toISOString() });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CardHeader className="text-center pb-2">
        <div className="mx-auto w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
          <GraduationCap className="w-8 h-8 text-white" />
        </div>
        <CardTitle className="text-2xl font-bold text-slate-800">Student Portal</CardTitle>
        <CardDescription className="text-slate-500">
          Login to view your class history and remaining credits
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-2">
            <Label htmlFor="username" className="text-slate-700 font-medium">Username</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="username"
                type="text"
                placeholder="Enter your username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="pl-10 h-11 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                data-testid="student-username-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-slate-700 font-medium">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="password"
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10 h-11 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                data-testid="student-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="toggle-password-btn"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium"
            data-testid="student-login-btn"
          >
            {loading ? "Signing in..." : "Sign In"}
          </Button>
        </form>

        {/* Connection error helper for login */}
        {connectionError && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl" data-testid="login-connection-error-help">
            <div className="flex items-start gap-2 mb-2">
              <WifiOff className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-700 text-sm">Connection Problem</p>
                <p className="text-red-600 text-xs mt-1">
                  We're having trouble connecting to our server from your location.
                  Please try using a VPN or contact your admin.
                </p>
              </div>
            </div>
            <a
              href="https://wa.me/639690740941?text=Hi%2C%20I%20cannot%20login%20to%20my%20student%20account.%20My%20username%20is%20${encodeURIComponent(username)}"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-4 py-2.5 rounded-lg transition-colors text-sm"
              data-testid="login-error-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              Contact Admin via WhatsApp
            </a>
          </div>
        )}

        {/* Detailed error diagnostic */}
        {errorDetail && !connectionError && (
          <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg relative" data-testid="student-login-error-detail">
            <button onClick={() => setErrorDetail(null)} className="absolute top-2 right-2 text-red-400 hover:text-red-600">
              <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
              <div className="min-w-0">
                <p className="font-semibold text-red-700 text-sm">{errorDetail.userMsg}</p>
                <p className="text-red-500 text-xs mt-1 break-all font-mono">{errorDetail.debugInfo}</p>
                <p className="text-red-400 text-[10px] mt-1">{errorDetail.timestamp}</p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-5">
          <button
            onClick={onSwitchToRegister}
            className="w-full flex items-center justify-center gap-2 h-11 rounded-md border border-emerald-200 text-emerald-700 hover:bg-emerald-50 font-medium text-sm transition-colors"
            data-testid="switch-to-register-btn"
          >
            <UserPlus className="w-4 h-4" />
            Create an Account
          </button>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-100">
          <p className="text-center text-sm text-slate-500">
            Are you a teacher or admin?{" "}
            <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-medium">
              Staff Login
            </Link>
          </p>
        </div>
      </CardContent>
    </>
  );
}

function RegisterForm({ onSwitchToLogin }) {
  const [name, setName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [referralCode, setReferralCode] = useState("");
  const { studentRegister } = useAuth();
  const navigate = useNavigate();

  const [connectionError, setConnectionError] = useState(false);

  // Auto-fill referral code from URL ?ref=
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref) setReferralCode(ref.toUpperCase());
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setConnectionError(false);
    if (!name || !username || !password || !email) {
      toast.error("Please fill in all fields including email");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }
    if (username.length < 3) {
      toast.error("Username must be at least 3 characters");
      return;
    }
    if (password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await studentRegister(name, username, password, referralCode, email);
      toast.success("Account created successfully! Welcome!");
      navigate("/student-dashboard");
    } catch (error) {
      if (error.response?.data?.detail) {
        // Server returned a clear error message
        toast.error(error.response.data.detail);
      } else if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
        // Network-level failure (CORS block, DDoS challenge, SSL issue)
        setConnectionError(true);
        toast.error("Connection error. Please check your internet or try a VPN.");
      } else if (error.code === "ECONNABORTED") {
        setConnectionError(true);
        toast.error("Request timed out. Please try again.");
      } else {
        setConnectionError(true);
        toast.error("Registration failed. Please contact support via WhatsApp.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <CardHeader className="text-center pb-2">
        <div className="mx-auto w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
          <UserPlus className="w-8 h-8 text-white" />
        </div>
        <CardTitle className="text-2xl font-bold text-slate-800">Create Account</CardTitle>
        <CardDescription className="text-slate-500">
          Register to access the Student Portal. Use your real name so we can link your classes.
        </CardDescription>
      </CardHeader>
      <CardContent className="pt-4">
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="reg-name" className="text-slate-700 font-medium">Full Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="reg-name"
                type="text"
                placeholder="Your full name (as registered)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="pl-10 h-11 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                data-testid="register-name-input"
              />
            </div>
            <p className="text-xs text-slate-400">Use the same name your admin registered you with to auto-link your records.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-username" className="text-slate-700 font-medium">Username</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="reg-username"
                type="text"
                placeholder="Choose a username (min 3 chars)"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="pl-10 h-11 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                data-testid="register-username-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-email" className="text-slate-700 font-medium">Email Address <span className="text-red-500">*</span></Label>
            <div className="relative">
              <MessageCircle className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="reg-email"
                type="email"
                placeholder="your@gmail.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-11 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                data-testid="register-email-input"
              />
            </div>
            <p className="text-xs text-slate-500">We'll send you booking confirmations and cancellation notifications.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-password" className="text-slate-700 font-medium">Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="reg-password"
                type={showPassword ? "text" : "password"}
                placeholder="Min 6 characters"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-10 pr-10 h-11 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                data-testid="register-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                data-testid="register-toggle-password-btn"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-confirm" className="text-slate-700 font-medium">Confirm Password</Label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                id="reg-confirm"
                type={showPassword ? "text" : "password"}
                placeholder="Re-enter your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="pl-10 h-11 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500"
                data-testid="register-confirm-password-input"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="reg-referral" className="text-slate-700 font-medium flex items-center gap-1">
              Referral Code <span className="text-xs text-slate-400 font-normal">(optional)</span>
            </Label>
            <Input
              id="reg-referral"
              type="text"
              placeholder="LANG-XXXXX"
              value={referralCode}
              onChange={(e) => setReferralCode(e.target.value.toUpperCase())}
              className="h-11 border-slate-200 focus:border-emerald-500 focus:ring-emerald-500 font-mono"
              data-testid="register-referral-input"
            />
            <p className="text-xs text-slate-400">Got a code from a friend? Enter it here so they earn rewards when you subscribe.</p>
          </div>
          <Button
            type="submit"
            disabled={loading}
            className="w-full h-11 bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700 text-white font-medium"
            data-testid="register-submit-btn"
          >
            {loading ? "Creating Account..." : "Create Account"}
          </Button>
        </form>

        {/* Connection error helper for students experiencing network issues */}
        {connectionError && (
          <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-xl" data-testid="connection-error-help">
            <div className="flex items-start gap-2 mb-2">
              <WifiOff className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="font-semibold text-red-700 text-sm">Connection Problem</p>
                <p className="text-red-600 text-xs mt-1">
                  We're having trouble connecting to our server from your location. 
                  Please try using a VPN or contact your admin to create your account.
                </p>
              </div>
            </div>
            <a
              href="https://wa.me/639690740941?text=Hi%2C%20I%20cannot%20create%20my%20student%20account.%20My%20name%20is%20${encodeURIComponent(name)}%20and%20I%20would%20like%20username%20${encodeURIComponent(username)}"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-2 w-full inline-flex items-center justify-center gap-2 bg-emerald-500 hover:bg-emerald-600 text-white font-semibold px-4 py-2.5 rounded-lg transition-colors text-sm"
              data-testid="connection-error-whatsapp"
            >
              <MessageCircle className="w-4 h-4" />
              Contact Admin via WhatsApp
            </a>
          </div>
        )}

        <div className="mt-5">
          <button
            onClick={onSwitchToLogin}
            className="w-full flex items-center justify-center gap-2 h-11 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 font-medium text-sm transition-colors"
            data-testid="switch-to-login-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Login
          </button>
        </div>

        <div className="mt-6 pt-6 border-t border-slate-100">
          <p className="text-center text-sm text-slate-500">
            Are you a teacher or admin?{" "}
            <Link to="/login" className="text-indigo-600 hover:text-indigo-700 font-medium">
              Staff Login
            </Link>
          </p>
        </div>
      </CardContent>
    </>
  );
}

export default function StudentLogin() {
  const [mode, setMode] = useState("login");

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-emerald-50 via-white to-teal-50 p-4">
      <Card className="w-full max-w-md shadow-xl border-0" data-testid="student-auth-card">
        {mode === "login" ? (
          <LoginForm onSwitchToRegister={() => setMode("register")} />
        ) : (
          <RegisterForm onSwitchToLogin={() => setMode("login")} />
        )}
      </Card>
    </div>
  );
}
